import React from 'react'
import { AgoraRtcCompatibilitySurface } from '../../providers/rtc/agora'
import SharedVideoCallShell from '../../ui/SharedVideoCallShell'
import { useRtcSessionHandleConfig } from '../useRtcSessionHandleConfig'

const EMPTY_OBJECT = Object.freeze({})

function invokeMaybe(handler, ...args) {
  if (typeof handler === 'function') {
    handler(...args)
  }
}

export default function SharedAgoraVideoCallSurface({
  shell,
  rtcProps,
  rtmProps,
  callbacks = EMPTY_OBJECT,
  styleProps = EMPTY_OBJECT,
  fallback = null,
  handleLeaveRoom,
  peerParticipantHint = null,
  layoutId = null,
  onLayoutChange = null,
  railStateId = null,
  onRailStateChange = null,
  showLayoutSelector = null,
  showRailSelector = null,
}) {
  const resolvedCallbacks = callbacks || EMPTY_OBJECT
  const resolvedStyleProps = styleProps || EMPTY_OBJECT
  const handleParticipantSnapshot = shell?.handleParticipantSnapshot
  const handleConnectionStateChange = shell?.handleConnectionStateChange
  const registerRuntimeCommands = shell?.registerRuntimeCommands
  const handleScreenShareStateChange = shell?.handleScreenShareStateChange
  const sessionHandle = shell?.sessionHandle
  const latestCallbacksRef = React.useRef(resolvedCallbacks)

  latestCallbacksRef.current = resolvedCallbacks

  const forwardParticipantSnapshot = React.useCallback(
    (snapshot) => {
      handleParticipantSnapshot?.(snapshot)
      invokeMaybe(latestCallbacksRef.current?.['participants-updated'], snapshot)
    },
    [handleParticipantSnapshot],
  )

  const forwardConnectionStateChange = React.useCallback(
    (sessionState, previousState, reason) => {
      handleConnectionStateChange?.(sessionState, previousState, reason)
      invokeMaybe(
        latestCallbacksRef.current?.['connection-state-change'],
        sessionState,
        previousState,
        reason,
      )
    },
    [handleConnectionStateChange],
  )

  const forwardRuntimeCommands = React.useCallback(
    (commands) => {
      registerRuntimeCommands?.(commands, sessionHandle)
      invokeMaybe(latestCallbacksRef.current?.['register-runtime-commands'], commands)
    },
    [registerRuntimeCommands, sessionHandle],
  )

  const forwardScreenShareStateChange = React.useCallback(
    (active) => {
      handleScreenShareStateChange?.(active)
      invokeMaybe(latestCallbacksRef.current?.['screen-share-state-changed'], active)
    },
    [handleScreenShareStateChange],
  )

  const mergedCallbacks = React.useMemo(
    () => ({
      ...resolvedCallbacks,
      'participants-updated': forwardParticipantSnapshot,
      'connection-state-change': forwardConnectionStateChange,
      'register-runtime-commands': forwardRuntimeCommands,
      'screen-share-state-changed': forwardScreenShareStateChange,
    }),
    [
      resolvedCallbacks,
      forwardConnectionStateChange,
      forwardParticipantSnapshot,
      forwardRuntimeCommands,
      forwardScreenShareStateChange,
    ],
  )
  const compatibilityConfig = useRtcSessionHandleConfig(shell?.sessionHandle)
  const resolvedRtcProps = compatibilityConfig?.rtcProps || rtcProps
  const resolvedRtmProps = compatibilityConfig?.rtmProps || rtmProps
  const agoraSurfaceKey = React.useMemo(
    () =>
      [
        resolvedRtcProps?.appId || '',
        resolvedRtcProps?.channel || '',
        resolvedRtcProps?.uid ?? '',
        resolvedRtmProps?.uid || '',
      ].join(':'),
    [
      resolvedRtcProps?.appId,
      resolvedRtcProps?.channel,
      resolvedRtcProps?.uid,
      resolvedRtmProps?.uid,
    ],
  )

  return (
    <AgoraRtcCompatibilitySurface
      key={agoraSurfaceKey}
      fallback={fallback}
      renderless
      rtcProps={resolvedRtcProps}
      rtmProps={resolvedRtmProps}
      styleProps={resolvedStyleProps}
      callbacks={mergedCallbacks}
    >
      <SharedVideoCallShell
        shell={shell}
        sessionInterfaceId={shell?.sessionInterfaceId}
        peerParticipantHint={peerParticipantHint}
        handleLeaveRoom={handleLeaveRoom}
        providerFallback={fallback}
        layoutId={layoutId}
        railStateId={railStateId}
        onLayoutChange={onLayoutChange}
        onRailStateChange={onRailStateChange}
        showLayoutSelector={showLayoutSelector}
        showRailSelector={showRailSelector}
      />
    </AgoraRtcCompatibilitySurface>
  )
}

SharedAgoraVideoCallSurface.displayName = 'SharedAgoraVideoCallSurface'
